
import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { LOCALE_ID, Inject } from '@angular/core';
import { CurrentPollService } from './current-poll.service';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { TranslationGeneralService } from '../../shared/translation/translation.general.service';
import { environment } from 'projects/whocan-app/src/environments/environment';
import { paths } from 'functions-lib';
import { EssErrorService } from 'ngx-essentia';
import { markLanguageSwitch } from '../../shared/services/lang-pref';
import { AccessGrantService, SelfAccessLinkParts } from './access-grant.service';

/**
 * Angular route of the assign access page (legacy account link). Not localised — the functions
 * (access-link.ts) carry the same constant. The app composes no new links on it since 1.5; the route
 * stays for the links already out there (AssignAccessComponent, support skill).
 */
export const ACCESS_LINK_ROUTE = 'user/assign-access';
/**
 * Angular routes of the join pages (poll / vote scoped access, workorder 1.2 task 5). Not localised —
 * the functions (1.5) build the mail links on the same constant values, so the two must not drift apart.
 */
export const JOIN_POLL_ROUTE = 'user/join-poll';
export const JOIN_VOTE_ROUTE = 'user/join-vote';

/** query parameters of a join-poll link */
export interface JoinPollLinkParts {
    ownerId: string;
    pollId: string;
    key: string;
    route: string;
}

/** query parameters of a join-vote link */
export interface JoinVoteLinkParts extends JoinPollLinkParts {
    voterId: string;
}

/** the strongest self access key of the current poll plus the route the join page redirects to */
export type JoinLinkParts = SelfAccessLinkParts & { route: string };


@Injectable({ providedIn: 'root' })
export class NavigationService {
    private history: string[] = [];
    constructor(
        private logger: LoggerService,
        @Inject(LOCALE_ID) public locale: string,
        private currentPollService: CurrentPollService,
        private trans: TranslationGeneralService,
        private router: Router,
        private location: Location,
        private errorService: EssErrorService,
        private accessGrantService: AccessGrantService,
    ) {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.logger.log('instanceof NavigationEnd', event.urlAfterRedirects, this.history);
                this.history.push(event.urlAfterRedirects);
            }
        });
    }

    // getPath
    // getPath

    private getPathHome() {
        return '/' + this.trans.getRoutePath('homeTemplates')
    }

    private getPathCreatePoll() {
        return '/' + this.trans.getRoutePath('createPoll')
    }

    /**
     * Ohne fuehrenden Doppel-Slash: getRoutePath() setzt den Slash schon selbst.
     * Fuer router.navigate() ist '//my-polls' egal, dieser Pfad geht aber zusaetzlich
     * als `linkRoute` in die Access-Link-Queue und damit durch die Rules.
     *
     * getPathHome/CreatePoll/ChoosePollTemplate oben haben denselben doppelten Slash.
     * Die landen nur im Router und sind hier bewusst nicht mitgeaendert — eigener
     * Aufraeum-Schritt, nicht mitten in einem Rollout.
     */
    private getPathMyPolls() {
        return this.trans.getRoutePath('myPolls')
    }

    private getPathChoosePollTemplate() {
        return '/' + this.trans.getRoutePath('choosePollTemplate')
    }
    private getPathEditPoll(step: string, ownerId?: string, pollId?: string) {
        return this.trans.getRoutePath('editPoll')
            + '/' + step + '/' + this.getOwnerSlashPollId(ownerId, pollId);
    }

    /**
     * Public, weil SamplePollCopyService den Pfad fuer `window.open(url, '_blank')`
     * im Embed-Mode (iframe) braucht — dort ist Angular-Router-Navigation nicht
     * geeignet, weil sie nur den iframe weiternavigieren wuerde, nicht den Top-Tab.
     */
    public getPathVotePreview(ownerId?: string, pollId?: string, voteUserId?: string) {
        return this.trans.getRoutePath('votePreview')
            + '/' + this.getOwnerSlashPollSlashVoteUserId(ownerId, pollId, voteUserId);
    }

    private getPathVotePreviewResponse(ownerId?: string, pollId?: string, voteUserId?: string) {
        return this.trans.getRoutePath('votePreviewResponse')
            + '/' + this.getOwnerSlashPollSlashVoteUserId(ownerId, pollId, voteUserId);
    }

    public getPathVote(ownerId?: string, pollId?: string, voteUserId?: string) {
        return this.trans.getRoutePath('vote')
            + '/' + this.getOwnerSlashPollSlashVoteUserId(ownerId, pollId, voteUserId);
    }

    /**
     * not specific to a user vote, this is what we need to share a
     */
    private getPathVoteGeneric(ownerId?: string, pollId?: string, voteUserId?: string) {
        return this.trans.getRoutePath('vote')
            + '/' + this.getOwnerSlashPollId(ownerId, pollId);
    }

    private getPathVoteResponse(ownerId?: string, pollId?: string, voteUserId?: string) {
        return this.trans.getRoutePath('voteResponse')
            + '/' + this.getOwnerSlashPollSlashVoteUserId(ownerId, pollId, voteUserId);
    }

    /**
     * The vote preview of a poll without a vote user id (route `votePreview/:ownerId/:pollId`): the target of
     * the calendar link. A composed link does not know who opens it - the page takes the opener's uid.
     */
    public getPathPollPreview(ownerId?: string, pollId?: string) {
        return this.trans.getRoutePath('votePreview')
            + '/' + this.getOwnerSlashPollId(ownerId, pollId);
    }

    /**
     * The edit view: the poll preview with the settings open - the same page as the edit icon of the
     * context bar and the deadline mail. Target of every poll join link (workorder poll-scoped access 1.8).
     */
    public getPathEditView(ownerId?: string, pollId?: string) {
        return this.getPathPollPreview(ownerId, pollId) + '?edit=true';
    }

    public getPathPollResponses(ownerId?: string, pollId?: string) {
        return this.trans.getRoutePath('pollResponses')
            + '/' + this.getOwnerSlashPollId(ownerId, pollId);
    }
    public getPathPollMatrix(ownerId?: string, pollId?: string) {
        return this.trans.getRoutePath('pollMatrix')
            + '/' + this.getOwnerSlashPollId(ownerId, pollId);
    }

    // navigate
    // navigate

    public navigate(routePath: string) {
        this.router.navigate([routePath]);
    }

    public navigateHome() {
        this.router.navigate([this.getPathHome()]);
    }
    public navigateMyPolls() {
        this.router.navigate([this.getPathMyPolls()]);
    }
    public navigateChoosePollTemplate(title?: string) {
        const queryParams = title ? { title } : undefined;
        this.router.navigate([this.getPathChoosePollTemplate()], { queryParams });
    }

    public navigatePollResponses(ownerId?: string, pollId?: string) {
        this.router.navigate([this.getPathPollResponses(ownerId, pollId)]);
    }

    public navigateVote(ownerId?: string, pollId?: string, voteUserId?: string) {
        this.router.navigate([this.getPathVote(ownerId, pollId, voteUserId)]);
    }

    public navigatePollMatrix(ownerId?: string, pollId?: string) {
        this.router.navigate([this.getPathPollMatrix(ownerId, pollId)]);
    }

    /**
     * Navigiert zur Sample-Poll-Detail-Page (Vote-View) anhand des Slugs.
     * Sprache = aktuelle User-Sprache via `trans.getRoutePath`; der Resolver
     * faellt automatisch auf EN zurueck, wenn die Variante in der User-Sprache
     * nicht existiert.
     */
    public navigateSamplePollDetail(slug: string) {
        const listing = this.trans.getRoutePath('samplePollsList'); // z.B. "/umfrage-vorlagen"
        this.router.navigateByUrl(`${listing}/${slug}`);
    }

    /**
     * Navigiert zur Sample-Poll-Matrix-Page (Ergebnis-View) anhand des Slugs.
     * Listing-Slug + Matrix-Suffix sind beide sprachspezifisch (z.B.
     * /umfrage-vorlagen/{slug}/ergebnisse bzw. /poll-templates/{slug}/results).
     *
     * navigateByUrl statt navigate(array), weil getRoutePath sowohl fuer
     * listing als auch fuer suffix einen fuehrenden Slash liefert — als
     * Array-Segment wuerde der Slash zu %2F encoded und Router 404'en.
     */
    public navigateSamplePollMatrix(slug: string) {
        const listing = this.trans.getRoutePath('samplePollsList'); // z.B. "/umfrage-vorlagen"
        const suffix = this.trans.getRoutePath('samplePollMatrixSuffix'); // z.B. "/ergebnisse"
        this.router.navigateByUrl(`${listing}/${slug}${suffix}`);
    }

    public navigateVotePreview(ownerId?: string, pollId?: string, voteUserId?: string, sidenavIsOpen?: boolean) {
        if (sidenavIsOpen === true) {
            this.router.navigate([this.getPathVotePreview(ownerId, pollId, voteUserId)], { queryParams: { edit: 'true' } });
        } else {
            this.router.navigate([this.getPathVotePreview(ownerId, pollId, voteUserId)]);
        }
        return;
    }

    public navigateVotePreviewResponse(ownerId?: string, pollId?: string, voteUserId?: string) {
        this.router.navigate([this.getPathVotePreviewResponse(ownerId, pollId, voteUserId)]);
    }

    public navigateVoteResponse(ownerId?: string, pollId?: string, voteUserId?: string) {
        this.router.navigate([this.getPathVoteResponse(ownerId, pollId, voteUserId)]);
    }

    public navigateEditPoll(step: string, ownerId?: string, pollId?: string) {
        this.router.navigate([this.getPathEditPoll(step, ownerId, pollId)]);
    }

    public navigateCreatePoll(pollTitle?: string, template?: string, themeKey?: string) {
        let routePath = this.getPathCreatePoll();
        if (pollTitle) {
            routePath = routePath + '/' + encodeURIComponent(pollTitle);
        }
        if (template) {
            this.logger.log('navigateCreatePoll', pollTitle, template, themeKey, routePath);
            this.router.navigate([routePath], { queryParams: { template: template, theme: themeKey } });
        } else {
            this.router.navigate([routePath]);
        }
    }

    /**
     * navigate backward in broser history if it is inside of the the app
     * @returns true if the navigation back was possible, false otherwise
     */
    public navigateBack(): boolean {
        this.logger.log('navigateBack', this.history);
        this.history.pop();
        if (this.history.length > 0) {
            this.logger.log('navigateBack this.history.length > 0)', this.history[this.history.length - 1]);
            //  this.navigate(this.history[this.history.length - 1]);
            this.location.back();
            this.logger.log('this.location.back()', this.history);
            return true;
        } else {
            return false;
        }
    }



    // getURL
    // getURL

    // Return the domain depending on evironment (prod or test) including locale
    public getUrlHost(): string {
        if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
            return "http://" + location.hostname + ":" + location.port
        }

        // Jeder Build traegt sein Sprach-Prefix, auch der englische ('en').
        // Der frueher hier stehende `!== 'en-US'`-Sonderfall konnte nie greifen:
        // LOCALE_ID ist 'en', nicht 'en-US'.
        if (this.locale) {
            return environment.host_url + '/' + this.locale;
        }
        return environment.host_url;
    }

    public getUrlPollResponses(ownerId?: string, pollId?: string) {
        return this.getUrlHost() +
            this.getPathPollResponses(ownerId, pollId)
    }

    public getUrlEditPoll(ownerId?: string, pollId?: string) {
        return this.getUrlHost() + this.getPathEditPoll('0', ownerId, pollId);
    }

    public getUrlVotePreview(ownerId?: string, pollId?: string) {
        return this.getUrlHost() + this.getPathVotePreview(ownerId, pollId)
    }

    public getUrlVote(ownerId?: string, pollId?: string) {
        return this.getUrlHost() + this.getPathVote(ownerId, pollId)
    }

    public getUrlSharePoll(ownerId?: string, pollId?: string) {
        return this.getUrlHost() + this.getPathVoteGeneric(ownerId, pollId)
    }

    // join links (poll / vote scoped access)
    // join links (poll / vote scoped access)

    /** `/{lang}/user/join-poll?owner=…&poll=…&key=…&route=…` — query names are shared with the join page and 1.5 */
    public composeUrlJoinPoll(parts: JoinPollLinkParts): string {
        const tree = this.router.createUrlTree([JOIN_POLL_ROUTE], {
            queryParams: { owner: parts.ownerId, poll: parts.pollId, key: parts.key, route: parts.route }
        });
        return this.getUrlHost() + this.router.serializeUrl(tree);
    }

    /** `/{lang}/user/join-vote?owner=…&poll=…&voter=…&key=…&route=…` */
    public composeUrlJoinVote(parts: JoinVoteLinkParts): string {
        const tree = this.router.createUrlTree([JOIN_VOTE_ROUTE], {
            queryParams: { owner: parts.ownerId, poll: parts.pollId, voter: parts.voterId, key: parts.key, route: parts.route }
        });
        return this.getUrlHost() + this.router.serializeUrl(tree);
    }

    /**
     * The strongest self access link on the current poll (concept §2.3 Z2): owner / legacy / co-owner ->
     * the organizer link (poll link with the ownerKey - the one poll key since 1.9, dialog, mail and calendar
     * alike), an editor grant from old dev data -> poll link with its own key, otherwise the vote link of the
     * current vote user when it is the own answer or a held vote grant; null when the user holds nothing.
     * Default route: the edit view for a poll link (1.8), the vote page for a vote link; `pollRoute` replaces
     * only the poll link's route - a vote link always opens the vote page. The surfaces call this plus
     * composeJoinUrl (1.4).
     */
    public async getJoinLinkParts(pollRoute?: string): Promise<JoinLinkParts | null> {
        const ownerId = this.currentPollService.ownerId;
        const pollId = this.currentPollService.pollId;
        const parts = await this.accessGrantService.getStrongestSelfAccessLink(ownerId, pollId, this.currentPollService.getVoteUserId());
        if (!parts) return null;
        const route = parts.kind === 'poll'
            ? pollRoute || this.getPathEditView(ownerId, pollId)
            : this.getPathVote(ownerId, pollId, parts.voterId);
        return { ...parts, route };
    }

    /** the join url of getJoinLinkParts - poll or vote link by `kind` */
    public composeJoinUrl(parts: JoinLinkParts): string {
        return parts.kind === 'vote'
            ? this.composeUrlJoinVote({ ownerId: parts.ownerId, pollId: parts.pollId, voterId: parts.voterId, key: parts.key, route: parts.route })
            : this.composeUrlJoinPoll({ ownerId: parts.ownerId, pollId: parts.pollId, key: parts.key, route: parts.route });
    }

    /**
     * Set the url in the broswer without triggering any component to reinit or te router service to trigger an event
     * @param step
     * @param ownerId
     * @param pollId
     */
    public goEditPoll(step: string, ownerId?: string, pollId?: string) {
        const routePath = this.getPathEditPoll(step, ownerId, pollId);
        this.location.go(routePath);
    }


    public openWindowLanguage(langCode: string) {
        const url = environment.host_url + '/' + langCode + this.getLanguageSwitchRoute()
        this.logger.log("navigateLanguage", url)
        // Einziger Choke-Point aller App-Sprachwechsel (Globe, Hamburger, Banner,
        // Profil-Card) — die Ziel-Seite zeigt auf vote*-Routen einmalig den
        // Chrome-≠-Inhalt-Hinweis (Konzept Prinzip 6).
        markLanguageSwitch();
        window.open(url, '_self');
    }

    /**
     * Route fuer den Sprachwechsel: die aktuelle Route (juengster History-Eintrag),
     * damit der Wechsel den Seiten-Kontext erhaelt. Fallback '': Sprach-Root.
     */
    private getLanguageSwitchRoute(): string {
        return this.history[this.history.length - 1] ?? '';
    }

    public getUrlPollMatrix(ownerId?: string, pollId?: string) {
        if ((ownerId && pollId) || this.currentPollService.isInitialized) {
            return this.getUrlHost() + this.trans.getRoutePath('pollMatrix') + '/'
                + this.getOwnerSlashPollId(ownerId, pollId);
        } else {
            return '';
        }

    }



    public get functionsUrlPaypalStart(): string {
        return environment.firebase.functionsUrl + "/" + paths.functions.paypalStart
    }

    private getOwnerSlashPollId(ownerId?: string, pollId?: string): string {
        this.logger.log('getOwnerSlashPollId: ', ownerId, pollId);
        return this.currentPollService.getOwnerId(ownerId) + '/' + this.currentPollService.getPollId(pollId);
    }

    private getOwnerSlashPollSlashVoteUserId(ownerId?: string, pollId?: string, userId?: string): string {
        return this.getOwnerSlashPollId(ownerId, pollId) + '/' + this.currentPollService.getVoteUserId(userId);
    }
}
